// `/app/transactions` (`spec/17-frontend.md` §8.1). Foco: **a tabela**.
//
// Esta é a rota-PAI: ela desenha a lista e, por cima, o `RouteDrawer` com o `<Outlet/>`
// da rota `$id` (`spec/24` §1 decisão 5). Deep link em `.../txn_…` monta a lista embaixo
// e o drawer aberto; fechar volta para cá preservando o `search`.
//
// Só roteador: o schema de `search`, a navegação e o drawer. A tela é
// `features/transactions/transactions-screen.tsx`.
//
// Os filtros vivem na query string (`spec/17` §3), não em `useState`: recarregar a
// página, compartilhar o link e o botão de voltar continuam valendo. O schema mora aqui
// (e não no `index`) porque o `$id` é filho desta rota e herda o mesmo `search`.

import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { RouteDrawer } from '../components/app/route-drawer.tsx';
import type { FilterValue } from '../features/transactions/filter-bar.tsx';
import { rangeForPreset } from '../features/transactions/filter-bar.tsx';
import { TRANSACTION_STATUS_FILTERS } from '../features/transactions/status.ts';
import { useTransactionDrawerHeader } from '../features/transactions/transaction-detail.tsx';
import { TransactionsScreen } from '../features/transactions/transactions-screen.tsx';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Query string tolerante a lixo: `?status=banana` cai em `undefined` em vez de derrubar
 * a rota. `catch` do Zod é o que faz isso sem `try`.
 *
 * `status` só aceita os 5 valores que o painel filtra (API-30, Caio 17/09): `expired`
 * saiu da lista, e um link velho com `?status=expired` cai em `undefined` pelo mesmo
 * `catch`, em vez de mandar `status=expired` para a API e tomar 400.
 */
const searchSchema = z.object({
  status: z.enum(TRANSACTION_STATUS_FILTERS).optional().catch(undefined),
  from: z.string().regex(ISO_DATE).optional().catch(undefined),
  to: z.string().regex(ISO_DATE).optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  /** Qual data o período filtra (croqui: `Select` "Criada em"). `created` é o default. */
  field: z.enum(['created', 'paid']).optional().catch(undefined),
});

/** Período default (30 dias) quando a URL não traz `from`/`to`. */
const DEFAULT_PRESET = '30d' as const;

function Transactions() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  // A rota `$id` é filha desta: se ela casa, o detalhe está aberto por cima da lista.
  const detail = useMatch({ from: '/app/transactions/$id', shouldThrow: false });
  const header = useTransactionDrawerHeader(detail?.params.id);

  const fallback = rangeForPreset(DEFAULT_PRESET);
  const value: FilterValue = {
    status: search.status,
    from: search.from ?? fallback.from,
    to: search.to ?? fallback.to,
    q: search.q,
    field: search.field ?? 'created',
  };

  // "Vazio" e "filtro sem resultado" são estados diferentes (`spec/17` §8.1): o período
  // default não conta como filtro, o que o usuário mexeu conta.
  const filtered =
    value.status !== undefined ||
    value.q !== undefined ||
    value.field !== 'created' ||
    value.from !== fallback.from ||
    value.to !== fallback.to;

  const apply = (next: FilterValue): void => {
    void navigate({
      to: '/app/transactions',
      search: {
        ...(next.status === undefined ? {} : { status: next.status }),
        ...(next.from === fallback.from ? {} : { from: next.from }),
        ...(next.to === fallback.to ? {} : { to: next.to }),
        ...(next.q === undefined ? {} : { q: next.q }),
        ...(next.field === 'created' ? {} : { field: next.field }),
      },
      replace: true,
    });
  };

  return (
    <>
      <TransactionsScreen
        value={value}
        onChange={apply}
        filtered={filtered}
        {...(detail === undefined ? { selectedId: undefined } : { selectedId: detail.params.id })}
        onSelect={(row) => {
          // `search` explícito: o filtro da lista tem de sobreviver à abertura do
          // drawer, e o `$id` herda o schema desta rota.
          void navigate({ to: '/app/transactions/$id', params: { id: row.id }, search });
        }}
        onClearFilters={() => {
          void navigate({ to: '/app/transactions', search: {}, replace: true });
        }}
        onCreated={(id) => {
          void navigate({ to: '/app/transactions/$id', params: { id } });
        }}
      />

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          void navigate({ to: '/app/transactions', search });
        }}
        title={header.title}
        sub={header.sub}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/app/transactions')({
  validateSearch: searchSchema,
  component: Transactions,
});
